import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLink, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { FilterChips, type FilterChip } from '@/components/FilterChips';
import { TicketCard } from '@/components/cards/TicketCard';
import { PriorityTag } from '@/components/PriorityTag';
import { AssignedResourcePicker } from '@/components/AssignedResourcePicker';
import { InlineEdit } from '@/components/InlineEdit';
import { PageHeader } from '@/components/PageHeader';
import { useTickets, usePicklists, useResources, useCompanyLookup, type TicketFilters } from '@/api/hooks';
import { dueState, fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';
import { ROUTES, ticketPath } from '@/lib/routes';
import { TicketDescriptionCell } from '@/components/TicketDescriptionCell';

const AUTOTASK_TICKET_URL = 'https://ww31.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=';
const SUPPORT_QUEUE_ID = 29682833;

const EMPTY_FILTERS: TicketFilters = {
  assignedTo: 'any', status: [], excludeStatus: [], queueID: [], priority: [], search: '',
};

type SortKey = 'id' | 'title' | 'status' | 'priority' | 'lastActivityDate';
type SortDir = 'asc' | 'desc';

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const csvNums = (v: string | null) => (v ? v.split(',').map(Number).filter(Number.isFinite) : []);

// URL <-> TicketFilters. Lets dashboard tiles deep-link a pre-filtered, auto-run search.
function paramsToTicketFilters(sp: URLSearchParams): { filters: TicketFilters; hasDrill: boolean } {
  const filters: TicketFilters = {
    assignedTo: sp.get('assignedTo') === 'me' ? 'me' : 'any',
    status: csvNums(sp.get('status')),
    excludeStatus: csvNums(sp.get('excludeStatus')),
    queueID: csvNums(sp.get('queueID')),
    priority: csvNums(sp.get('priority')),
    search: sp.get('search') ?? '',
    companyID: sp.get('companyID') ? Number(sp.get('companyID')) : undefined,
    resourceID: sp.get('resourceID') ? Number(sp.get('resourceID')) : undefined,
    createdAfter: sp.get('createdAfter') ?? undefined,
    createdBefore: sp.get('createdBefore') ?? undefined,
    dueBefore: sp.get('dueBefore') ?? undefined,
    completedAfter: sp.get('completedAfter') ?? undefined,
    completedBefore: sp.get('completedBefore') ?? undefined,
    unassigned: sp.get('unassigned') === '1' || sp.get('unassigned') === 'true' || undefined,
  };
  const hasDrill = [...sp.keys()].some((k) => k !== 'assignedTo');
  return { filters, hasDrill };
}

function ticketFiltersToParams(f: TicketFilters): URLSearchParams {
  const p = new URLSearchParams();
  p.set('assignedTo', f.assignedTo);
  if (f.status.length) p.set('status', f.status.join(','));
  if (f.excludeStatus.length) p.set('excludeStatus', f.excludeStatus.join(','));
  if (f.queueID.length) p.set('queueID', f.queueID.join(','));
  if (f.priority.length) p.set('priority', f.priority.join(','));
  if (f.search) p.set('search', f.search);
  if (f.companyID) p.set('companyID', String(f.companyID));
  if (f.resourceID) p.set('resourceID', String(f.resourceID));
  if (f.createdAfter) p.set('createdAfter', f.createdAfter);
  if (f.createdBefore) p.set('createdBefore', f.createdBefore);
  if (f.dueBefore) p.set('dueBefore', f.dueBefore);
  if (f.completedAfter) p.set('completedAfter', f.completedAfter);
  if (f.completedBefore) p.set('completedBefore', f.completedBefore);
  if (f.unassigned) p.set('unassigned', '1');
  return p;
}

export default function TicketSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Hydrate from the URL once on mount — dashboard drill-downs arrive here pre-filtered.
  const initial = useMemo(() => paramsToTicketFilters(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [text, setText] = useState(initial.hasDrill ? initial.filters.search : '');
  const [company, setCompany] = useState<CompanyOption | null>(null);
  const [resourceID, setResourceID] = useState(initial.filters.resourceID ? String(initial.filters.resourceID) : '');
  const [queueID, setQueueID] = useState(initial.hasDrill ? (initial.filters.queueID[0] ? String(initial.filters.queueID[0]) : '') : String(SUPPORT_QUEUE_ID));
  const [createdAfter, setCreatedAfter] = useState(initial.filters.createdAfter ? initial.filters.createdAfter.slice(0, 10) : '');
  const [openOnly, setOpenOnly] = useState(initial.hasDrill ? initial.filters.excludeStatus.includes(5) : true);

  const [submitted, setSubmitted] = useState<TicketFilters | null>(initial.hasDrill ? initial.filters : null);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'lastActivityDate', dir: 'desc' });

  // Apply a filter set: drive the query and keep the URL in sync (so chips, refresh, and
  // bookmarks all reflect the active filters).
  const applyFilters = (next: TicketFilters) => {
    setSubmitted(next);
    setSearchParams(ticketFiltersToParams(next), { replace: true });
  };

  const picklists = usePicklists();
  const resources = useResources();
  const tickets = useTickets(submitted ?? EMPTY_FILTERS, { enabled: submitted !== null, scoped: true });

  const items = tickets.data?.items ?? [];
  const companyIds = useMemo(() => [...new Set(items.map((t) => t.companyID).filter((v): v is number => v != null))], [items]);
  const companies = useCompanyLookup(companyIds);

  const companyMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName);
    return m;
  }, [companies.data]);

  const resourceMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of resources.data?.items ?? []) m.set(r.id, `${r.firstName} ${r.lastName}`.trim());
    return m;
  }, [resources.data]);

  const pl = picklists.data;
  const statusMap  = useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.status   ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);
  const priorityMap= useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.priority ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);
  const queueMap   = useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.queueID  ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);

  const sortedItems = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      let va: string | number, vb: string | number;
      switch (sort.key) {
        case 'id':              va = a.id;                                               vb = b.id; break;
        case 'title':           va = (a.title ?? '').toLowerCase();                      vb = (b.title ?? '').toLowerCase(); break;
        case 'status':          va = statusMap.get(a.status) ?? '';                      vb = statusMap.get(b.status) ?? ''; break;
        case 'priority':        va = priorityMap.get(a.priority) ?? '';                  vb = priorityMap.get(b.priority) ?? ''; break;
        case 'lastActivityDate':va = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
                                vb = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0; break;
        default: return 0;
      }
      if (va < vb) return -dir; if (va > vb) return dir; return 0;
    });
  }, [items, sort, statusMap, priorityMap]);

  const cycleSort = (key: SortKey) =>
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'lastActivityDate' ? 'desc' : 'asc' });

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    applyFilters({
      assignedTo: 'any',
      search: text.trim(),
      status: [],
      excludeStatus: openOnly ? [5, 21] : [],
      queueID: queueID ? [Number(queueID)] : [],
      priority: [],
      companyID: company?.id,
      resourceID: resourceID ? Number(resourceID) : undefined,
      createdAfter: createdAfter ? new Date(createdAfter).toISOString() : undefined,
    });
  };

  const clearSearch = () => {
    setText(''); setCompany(null); setResourceID('');
    setQueueID(String(SUPPORT_QUEUE_ID)); setCreatedAfter(''); setOpenOnly(true);
    setSubmitted(null);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  // Removable chips for every active filter (drill-down dims included). Removing one
  // re-runs the query without it; "Clear all" resets to the empty search.
  const chips: FilterChip[] = [];
  if (submitted) {
    const f = submitted;
    const patch = (p: Partial<TicketFilters>) => applyFilters({ ...f, ...p });
    if (f.search) chips.push({ key: 'search', label: `“${f.search}”`, onRemove: () => patch({ search: '' }) });
    f.status.forEach((id) => chips.push({ key: `status-${id}`, label: `Status: ${statusMap.get(id) ?? id}`, onRemove: () => patch({ status: f.status.filter((x) => x !== id) }) }));
    if (f.excludeStatus.length) chips.push({ key: 'excl', label: 'Open only', onRemove: () => patch({ excludeStatus: [] }) });
    f.queueID.forEach((id) => chips.push({ key: `q-${id}`, label: `Queue: ${queueMap.get(id) ?? id}`, onRemove: () => patch({ queueID: f.queueID.filter((x) => x !== id) }) }));
    f.priority.forEach((id) => chips.push({ key: `p-${id}`, label: `Priority: ${priorityMap.get(id) ?? id}`, onRemove: () => patch({ priority: f.priority.filter((x) => x !== id) }) }));
    if (f.resourceID) chips.push({ key: 'res', label: `Assignee: ${resourceMap.get(f.resourceID) ?? `#${f.resourceID}`}`, onRemove: () => patch({ resourceID: undefined }) });
    if (f.unassigned) chips.push({ key: 'unassigned', label: 'Unassigned', onRemove: () => patch({ unassigned: undefined }) });
    if (f.assignedTo === 'me') chips.push({ key: 'me', label: 'My tickets', onRemove: () => patch({ assignedTo: 'any' }) });
    if (f.createdAfter) chips.push({ key: 'ca', label: `Created ≥ ${f.createdAfter.slice(0, 10)}`, onRemove: () => patch({ createdAfter: undefined }) });
    if (f.createdBefore) chips.push({ key: 'cb', label: `Created < ${f.createdBefore.slice(0, 10)}`, onRemove: () => patch({ createdBefore: undefined }) });
    if (f.dueBefore) chips.push({ key: 'due', label: 'Overdue', onRemove: () => patch({ dueBefore: undefined }) });
    if (f.completedAfter) chips.push({ key: 'cma', label: `Completed ≥ ${f.completedAfter.slice(0, 10)}`, onRemove: () => patch({ completedAfter: undefined }) });
    if (f.completedBefore) chips.push({ key: 'cmb', label: `Completed < ${f.completedBefore.slice(0, 10)}`, onRemove: () => patch({ completedBefore: undefined }) });
    if (f.companyID) chips.push({ key: 'co', label: `Company #${f.companyID}`, onRemove: () => patch({ companyID: undefined }) });
  }

  const SortTh = ({ col, label }: { col: SortKey; label: string }) => (
    <th onClick={() => cycleSort(col)}
      className="cursor-pointer select-none px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground">
      {label}{sort.key === col && <span className="ml-1 opacity-60">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
  const PlainTh = ({ label }: { label: string }) => (
    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</th>
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
      <PageHeader
        title="Ticket Search"
        isFetching={tickets.isFetching}
        subtitle="Search across all tickets"
        actions={
          <Button size="sm" onClick={() => navigate(ROUTES.psa.tickets.new)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />New Ticket
          </Button>
        }
      />

      <form onSubmit={handleSearch} className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
        {/* Row 1: text + company + assignee */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Title, description or ticket #</label>
            <SearchInput value={text} onValueChange={setText} placeholder="Keyword, T20260529.0005, …" autoFocus={!initial.hasDrill} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Company</label>
            <CompanySearch value={company} onChange={setCompany} placeholder="Any company…" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Assignee</label>
            <select value={resourceID} onChange={(e) => setResourceID(e.target.value)} className={SELECT_CLS}>
              <option value="">Any assignee</option>
              {(resources.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: queue + created after + toggles + search */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Queue</label>
            <select value={queueID} onChange={(e) => setQueueID(e.target.value)} className={SELECT_CLS}>
              <option value="">Any queue</option>
              {(pl?.queueID ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Created after</label>
            <DatePicker size="md" className="w-full" value={createdAfter || null} onChange={(v) => setCreatedAfter(v ?? '')} placeholder="Any date" />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit">
              <Search className="mr-1.5 h-3.5 w-3.5" />Search
            </Button>
            {submitted && (
              <Button type="button" variant="ghost" size="icon" onClick={clearSearch} aria-label="Clear">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-input accent-primary" />
            Open tickets only (exclude completed)
          </label>
        </div>
      </form>

      <FilterChips chips={chips} onClear={clearSearch} />

      {/* Pre-search prompt */}
      {!submitted && (
        <div className="mt-12 flex flex-col items-center gap-2 text-center text-muted-foreground">
          <Search className="h-8 w-8 opacity-30" />
          <p className="text-sm">Set your filters above and click Search.</p>
        </div>
      )}

      {submitted && tickets.isLoading && (
        <div className="mt-8 flex justify-center text-sm text-muted-foreground">Searching…</div>
      )}

      {submitted && !tickets.isLoading && items.length === 0 && (
        <div className="mt-8 flex flex-col items-center gap-2 text-center text-muted-foreground">
          <p className="text-sm">No tickets found. Try adjusting your filters.</p>
        </div>
      )}

      {submitted && items.length > 0 && (
        <>
          <p className="mt-4 text-xs text-muted-foreground">{items.length} result{items.length !== 1 ? 's' : ''}</p>
          {/* Mobile */}
          <div className="mt-2 space-y-2 md:hidden">
            {sortedItems.map((t) => (
              <TicketCard key={t.id} t={t} companyMap={companyMap} statusMap={statusMap} priorityMap={priorityMap} queueMap={queueMap} />
            ))}
          </div>

          {/* Desktop */}
          <div className="mt-4 hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <SortTh col="id" label="#" />
                  <SortTh col="title" label="Title" />
                  <PlainTh label="Description" />
                  <PlainTh label="Company" />
                  <SortTh col="status" label="Status" />
                  <SortTh col="priority" label="Priority" />
                  <PlainTh label="Queue" />
                  <PlainTh label="Assignee" />
                  <SortTh col="lastActivityDate" label="Activity" />
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {sortedItems.map((t) => {
                  const priorityLabel = priorityMap.get(t.priority) ?? null;
                  const queueLabel = t.queueID != null ? queueMap.get(t.queueID) ?? null : null;
                  const ds = dueState(t.dueDateTime);
                  return (
                    <tr key={t.id} className={cn('group hover:bg-muted/30', ds === 'overdue' && 'rail-danger')}>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        <Link to={ticketPath(t.id)} className="hover:text-foreground hover:underline">{t.ticketNumber}</Link>
                      </td>
                      <td className="max-w-xs px-3 py-2">
                        <Link to={ticketPath(t.id)} className="line-clamp-2 text-sm font-medium hover:text-brand">{t.title}</Link>
                      </td>
                      <td className="px-3 py-2">
                        {/* Auto-layout table: the width has to sit on the content for the line to truncate. */}
                        <div className="w-[240px]"><TicketDescriptionCell description={t.description} /></div>
                      </td>
                      <td className="max-w-[160px] truncate px-3 py-2 text-xs text-muted-foreground">
                        {t.companyID != null ? companyMap.get(t.companyID) ?? '' : ''}
                      </td>
                      <td className="px-3 py-2">
                        <InlineEdit ticketId={t.id} field="status" value={t.status} options={pl?.status ?? []} variant="text" />
                      </td>
                      <td className="px-3 py-2">{priorityLabel && <PriorityTag priority={priorityLabel} />}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{queueLabel ?? '—'}</td>
                      <td className="px-3 py-2">
                        <AssignedResourcePicker ticketId={t.id} value={t.assignedResourceID ?? null} />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {t.lastActivityDate ? fmtRelative(t.lastActivityDate) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <a href={`${AUTOTASK_TICKET_URL}${t.id}`} target="_blank" rel="noreferrer"
                          className="opacity-0 group-hover:opacity-60 hover:!opacity-100" title="Open in Autotask">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
